import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Mail, Lock, Eye, EyeOff, ArrowRight, Loader2, ArrowLeft, CheckCircle } from 'lucide-react';

// Your assets
const COMPANY_LOGO_URL = '/ENSUElogoPNG.webp';
const LOGO_URL = '/ENSUE_LIVETRAK.webp';
const BACKGROUND_URL = '/blackbackground.webp';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  // Default ON (persisted): mobile PWAs lose sessionStorage whenever the OS kills
  // the backgrounded app, so an unchecked box means constant re-logins on phones.
  const [rememberMe, setRememberMe] = useState(() => localStorage.getItem('remember_me_pref') !== 'false');
  const [loading, setLoading] = useState(false);
  const [blockedError, setBlockedError] = useState(null);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBlockedError(null);
    
    if (!email || !password) {
      toast.error('Please enter email and password');
      return;
    }

    setLoading(true);
    try {
      await login(email, password, rememberMe);
      // Navigate to root — SmartRedirect will route to the first enabled page
      // based on FeatureFlagsContext (no duplicate API call needed)
      navigate('/');
    } catch (error) {
      console.error('Login error:', error);
      const errorMessage = error.response?.data?.detail || 'Invalid credentials';
      
      // Check if account is blocked/deactivated/expired
      const lowerMsg = errorMessage.toLowerCase();
      if (lowerMsg.includes('deactivated') || lowerMsg.includes('blocked')) {
        setBlockedError('deactivated');
      } else if (lowerMsg.includes('expired')) {
        setBlockedError('expired');
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error('Please enter your email address');
      return;
    }
    setForgotLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email: forgotEmail });
      setForgotSent(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset email');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
      style={{
        backgroundImage: `url(${BACKGROUND_URL})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
      data-testid="login-page"
    >
      {/* Dark overlay for better contrast */}
      <div className="absolute inset-0 bg-black/40" />

      {/* Login Card - 10% narrower */}
      <Card className="w-full max-w-[400px] relative z-10 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md shadow-2xl border-0 animate-fadeIn" data-testid="login-card">
        <CardContent className="p-8">
          {/* Logos */}
          <div className="text-center mb-8">
            {/* Company Logo on top - 20% bigger */}
            <div className="flex justify-center mb-2">
              <img 
                src={COMPANY_LOGO_URL} 
                alt="ENSUE" 
                className="h-14 w-auto object-contain"
                data-testid="company-logo"
              />
            </div>
            {/* EnsueLIVETRAK Logo */}
            <div className="flex justify-center mb-3">
              <img 
                src={LOGO_URL} 
                alt="EnsueLIVETRAK" 
                className="h-16 w-auto object-contain"
                data-testid="login-logo"
              />
            </div>
            <p className="text-sm text-slate-500 dark:text-slate-400">Sign in to continue</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Blocked/Expired Account Error Banner */}
            {blockedError && (
              <div className="bg-red-50 dark:bg-red-950/50 border border-red-200 dark:border-red-800 rounded-lg p-4 text-center" data-testid="blocked-error">
                <div className="flex flex-col items-center gap-2">
                  <div className="w-10 h-10 bg-red-100 dark:bg-red-900/50 rounded-full flex items-center justify-center">
                    <Lock className="w-5 h-5 text-red-600 dark:text-red-400" />
                  </div>
                  <div>
                    <p className="font-semibold text-red-700 dark:text-red-400">
                      {blockedError === 'expired' ? 'Account has expired' : 'Account is deactivated'}
                    </p>
                    <a href="mailto:admin@ensue.com.au?subject=EnsueLIVETRAK%20-%20Account%20Assistance" className="text-sm text-red-600 dark:text-red-500 font-bold mt-1 hover:underline">CONTACT ADMINISTRATOR</a>
                  </div>
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  data-testid="toggle-password"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Checkbox 
                  id="remember" 
                  checked={rememberMe}
                  onCheckedChange={(v) => {
                    setRememberMe(!!v);
                    localStorage.setItem('remember_me_pref', v ? 'true' : 'false');
                  }}
                  data-testid="remember-checkbox"
                />
                <Label htmlFor="remember" className="text-sm font-normal cursor-pointer">
                  Remember me
                </Label>
              </div>
              <button
                type="button"
                onClick={() => { setForgotOpen(true); setForgotSent(false); setForgotEmail(email); }}
                className="text-sm text-[#2563eb] hover:underline"
                data-testid="forgot-password-link"
              >
                Forgot password?
              </button>
            </div>

            <Button
              type="submit"
              className="w-full bg-[#2563eb] hover:bg-[#1d4ed8] text-white py-6 transition-all"
              disabled={loading}
              data-testid="login-submit-button"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              Sign In
              {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
            </Button>
          </form>

          {/* Footer */}
          <p className="text-center text-sm text-slate-500 mt-6">
            Don&apos;t have an account?{' '}
            <a
              href="mailto:admin@ensue.com.au?subject=EnsueLIVETRAK%20-%20Account%20Request"
              className="text-[#2563eb] hover:underline"
              data-testid="contact-admin-link"
            >
              Contact Administrator
            </a>
          </p>
        </CardContent>
      </Card>

      {/* Forgot Password Dialog */}
      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-[#2563eb]" />
              Reset Password
            </DialogTitle>
            <DialogDescription>
              {forgotSent ? 'Check your email for the reset link.' : 'Enter your email and we\'ll send you a password reset link.'}
            </DialogDescription>
          </DialogHeader>

          {forgotSent ? (
            <div className="text-center py-4 space-y-4">
              <div className="w-14 h-14 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-7 h-7 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  If an account with <strong>{forgotEmail}</strong> exists, a password reset link has been sent.
                </p>
                <p className="text-xs text-muted-foreground mt-2">The link expires in 1 hour.</p>
              </div>
              <Button variant="outline" onClick={() => setForgotOpen(false)} className="w-full">
                Close
              </Button>
            </div>
          ) : (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="forgot-email">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="forgot-email"
                    type="email"
                    placeholder="Enter your email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    className="pl-10"
                    autoFocus
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => setForgotOpen(false)} className="flex-1">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
                <Button type="submit" disabled={forgotLoading || !forgotEmail} className="flex-1 bg-[#2563eb] hover:bg-[#1d4ed8]" data-testid="send-reset-btn">
                  {forgotLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                  Send Reset Link
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
